import clsx from "clsx";
import FeatureBox from "components/conversion/FeatureBox";
import useSelfie from "lib/hooks/useSelfie";
import { NativeSelect } from "lib/miolo";
import * as React from "react";
import type { MenuProvided } from "react-instantsearch-core";
import { connectMenu } from "react-instantsearch-dom";
import { twMerge } from "tailwind-merge";

export default function AssetType() {
  return <AssetTypeMenu attribute="type" />;
}

const AssetTypeMenu = connectMenu<MenuProvided>(({ items, refine, currentRefinement }) => {
  const viewer = useSelfie();
  return (
    <div className="space-y-4">
      <div
        className={clsx("space-y-1 font-aeonik", {
          hidden: currentRefinement === null,
        })}
      >
        <h3 className="font-aeonik text-base font-bold uppercase tracking-wide text-zinc-500">Filtrar Por</h3>
        <span className="flex font-aeonik text-sm font-normal italic tracking-wider text-slate-400">
          Escolha um tipo de ativo.
        </span>
      </div>

      {currentRefinement !== null ? (
        <NativeSelect value={currentRefinement || ""} onChange={(event) => refine(event.currentTarget.value || "")}>
          <option value="">Todos</option>

          {items
            .filter((item) => getHumanReadableLabel(item.label))
            .sort(
              (a, b) =>
                SORT_BY.indexOf(getHumanReadableLabel(a.label) || "") -
                SORT_BY.indexOf(getHumanReadableLabel(b.label) || ""),
            )
            .map((item) => {
              return (
                <option key={item.label} value={item.label}>
                  {getHumanReadableLabel(item.label)}
                </option>
              );
            })}
        </NativeSelect>
      ) : (
        <div className="grid grid-cols-2 gap-6 lg:flex lg:grid-cols-2 lg:flex-col">
          {items
            .filter((item) => getHumanReadableLabel(item.label))
            .sort((a, b) => {
              return (
                SORT_BY.indexOf(getHumanReadableLabel(a.label) || "") -
                SORT_BY.indexOf(getHumanReadableLabel(b.label) || "")
              );
            })
            .map((item) => {
              return (
                <div
                  key={item.label}
                  className={twMerge(
                    "group relative",
                    "before:absolute before:-inset-1.5 before:rounded-[20px]",
                    "before:bg-violet-100 before:opacity-0 hover:before:opacity-100",
                  )}
                >
                  <button
                    type="button"
                    aria-label={`Filtrar por ${getHumanReadableLabel(item.label)}`}
                    onClick={() => refine(item.value || "")}
                    className={twMerge(
                      "group",
                      "relative cursor-pointer rounded-xl bg-slate-100 p-4 text-left font-aeonik",
                      "border border-transparent",
                      "w-full ring-1 ring-slate-500/20 hover:ring-violet-300",
                    )}
                  >
                    <div
                      className={twMerge(
                        "flex flex-col-reverse items-center justify-center text-center",
                        "md:flex-row md:justify-between",
                      )}
                    >
                      <div className="mt-2 space-y-1 md:mt-0">
                        <h5 className="flex justify-center text-sm font-bold text-slate-500 group-hover:text-violet-600 md:justify-start">
                          {getHumanReadableLabel(item.label)}
                        </h5>

                        <span className="flex justify-center text-sm font-medium text-slate-400 md:justify-start">
                          {item.count} ativos
                        </span>
                      </div>

                      {(item.value === "BRAZILIAN_COMPANY" || item.value === "BRAZILIAN_REAL_ESTATE_FUND") && (
                        <ChristTheRedeemer className="opacity-50 group-hover:opacity-100" />
                      )}

                      {(item.value === "AMERICAN_COMPANY" || item.value === "AMERICAN_REIT") && (
                        <StatueOfLiberty className="opacity-50 group-hover:opacity-100" />
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
        </div>
      )}

      {viewer && !viewer.isPremium && currentRefinement === null && (
        <FeatureBox
          displayOrigin="Screener"
          callToActionText={
            <>
              Turbine suas análises com <strong>dados exclusivos</strong> de mais de 6.000 ativos.
            </>
          }
          featureHighlights={[
            "Acesso ao Ranking completo",
            "Agenda de Dividendos",
            "Central de Resultados",
            "Histórico máximo de dados",
          ]}
        />
      )}
    </div>
  );
});

const SORT_BY = ["Ações", "FIIs", "Stocks", "REITs"];

function getHumanReadableLabel(machineReadableLabel: string): string | null {
  if (machineReadableLabel === "AMERICAN_COMPANY") {
    return "Stocks";
  }

  if (machineReadableLabel === "AMERICAN_REIT") {
    return "REITs";
  }

  if (machineReadableLabel === "BRAZILIAN_COMPANY") {
    return "Ações";
  }

  if (machineReadableLabel === "BRAZILIAN_REAL_ESTATE_FUND") {
    return "FIIs";
  }

  return null;
}

function ChristTheRedeemer(props: React.SVGProps<any>) {
  return (
    <svg width={25} height={35} viewBox="0 0 25 35" {...props}>
      <g fillRule="nonzero" fill="none">
        <path
          d="M20.109 32.813H4.89v-17.5c0-2.417 1.947-4.376 4.348-4.376h6.522c2.401 0 4.348 1.96 4.348 4.376v17.5z"
          fill="#C8F5F0"
        />
        <path fill="#A494A4" d="M8.152 27.89h8.696v4.376H8.152z" />
        <path
          d="M14.13 4.922V2.188c0-.907-.73-1.641-1.63-1.641-.9 0-1.63.734-1.63 1.64v2.735L3.456 6.414c-.491.099-.195.149-.195.149L4.43 8.505c.295.49.824.79 1.395.79h3.958v18.595h5.434V9.297h3.958c.57 0 1.1-.3 1.395-.791l1.17-1.944-.196-.148-7.414-1.492z"
          fill="#FFEACD"
        />
        <path fill="#FFD3AE" d="M15.217 21.328l-5.434-3.281v9.844h5.434z" />
        <path d="M5.978 33.36c0 .905.73 1.64 1.63 1.64h9.783c.9 0 1.63-.735 1.63-1.64H5.979z" fill="#D1C9D1" />
        <path
          d="M19.022 33.906H5.978c-.6 0-1.087-.49-1.087-1.093l.544-.547h14.13l.544.547a1.09 1.09 0 01-1.087 1.093z"
          fill="#A494A4"
        />
        <path
          d="M15.543 17.61l-1.521-1.15a1.101 1.101 0 01-.435-.874v-9.57c0-.302.244-.547.543-.547.3 0 .544-.245.544-.547a.545.545 0 00-.544-.547c-.899 0-1.63.736-1.63 1.64v1.456L9.235 4.944a.541.541 0 00-.762.1.55.55 0 00.1.767l3.927 3.04v1.523L6.31 5.582a.541.541 0 00-.763.1.55.55 0 00.1.767l6.853 5.305v3.832c0 .685.325 1.339.87 1.75l1.521 1.148a.538.538 0 00.761-.109.55.55 0 00-.109-.766z"
          fill="#7C657C"
        />
        <path
          d="M24.457 6.016h-1.421a7.04 7.04 0 01-1.386-.138l-6.976-1.404V2.187A2.183 2.183 0 0012.5 0a2.183 2.183 0 00-2.174 2.188v2.286L3.35 5.878a7.04 7.04 0 01-1.386.138H.544c-.3 0-.544.244-.544.546 0 .303.243.547.543.547h1.421c.319 0 .639-.02.956-.057L3.965 8.79a2.183 2.183 0 001.86 1.055h3.414V27.89c0 .302.243.547.544.547.3 0 .543-.245.543-.547v-8.878l4.348 2.625v6.253c0 .302.243.547.543.547.3 0 .544-.245.544-.547V9.844h3.414c.756 0 1.469-.404 1.86-1.055l1.045-1.737c.317.038.637.057.956.057h1.42c.3 0 .544-.244.544-.546a.545.545 0 00-.543-.547zm-4.352 2.207a1.092 1.092 0 01-.93.527h-3.958c-.3 0-.543.245-.543.547v11.065l-4.348-2.625v-8.44a.545.545 0 00-.543-.547H5.825c-.378 0-.735-.202-.93-.527l-.827-1.375 6.908-1.39a.546.546 0 00.437-.536V2.188c0-.604.488-1.094 1.087-1.094.6 0 1.087.49 1.087 1.093v2.735c0 .26.183.485.437.536l6.908 1.39-.827 1.375z"
          fill="#503250"
        />
        <path
          d="M16.848 27.344h-3.805v-6.69l-1.086-.657v7.347H8.152c-.3 0-.543.245-.543.547v4.375c0 .302.243.547.543.547.3 0 .544-.245.544-.547v-3.828h7.608v3.828c0 .302.244.547.544.547.3 0 .543-.245.543-.547V27.89a.545.545 0 00-.543-.547z"
          fill="#7C657C"
        />
        <path d="M20.109 32.833H4.9a1.09 1.09 0 011.087-1.094h13.035c.6 0 1.087.49 1.087 1.094z" fill="#503250" />
      </g>
    </svg>
  );
}

function StatueOfLiberty(props: React.SVGProps<any>) {
  return (
    <svg width={23} height={35} viewBox="0 0 23 35" {...props}>
      <g fillRule="nonzero" fill="none">
        <g fill="#368A50">
          <path d="M14.342 14.917a.53.53 0 01-.524-.536V9.774a.53.53 0 01.524-.536c.29 0 .524.24.524.536v4.607a.53.53 0 01-.524.536zM16.234 15.454a.513.513 0 01-.27-.077.543.543 0 01-.178-.735l1.956-3.324a.517.517 0 01.719-.183.543.543 0 01.179.736l-1.956 3.324a.522.522 0 01-.45.26zM17.342 16.506a.52.52 0 01-.429-.227.543.543 0 01.127-.746l2.567-1.853a.517.517 0 01.73.13.543.543 0 01-.125.746l-2.568 1.852a.513.513 0 01-.302.098zM17.862 17.604a.538.538 0 01-.149-1.05l2.078-.629a.522.522 0 01.65.362.538.538 0 01-.353.666l-2.077.629a.509.509 0 01-.15.022zM12.45 15.454a.521.521 0 01-.449-.26l-1.956-3.323a.543.543 0 01.179-.736.518.518 0 01.719.183l1.956 3.324c.15.254.07.583-.179.735a.513.513 0 01-.27.077zM11.343 16.506a.514.514 0 01-.302-.098l-2.568-1.852a.543.543 0 01-.126-.747.517.517 0 01.73-.129l2.568 1.853c.237.17.293.505.126.746a.52.52 0 01-.428.227zM10.823 17.604c-.05 0-.1-.007-.149-.022l-2.077-.63a.538.538 0 01-.354-.665.522.522 0 01.651-.361l2.078.629a.538.538 0 01-.149 1.05zM10.675 29.09V18.132h7.335v13.96" />
        </g>
        <path
          fill="#5EBC77"
          d="M20.889 31.026l1.208-1.128-3.354-4.714h-6.811l-3.248-1.482-3.616-12.606H3.444l1.991 16.767-1.572 2.013 2.41 3.022V35h16.662z"
        />
        <g fill="#368A50">
          <path d="M7.157 25.658L3.624 12.607l1.811 15.256" />
          <path d="M8.684 23.702l-3.146 8.274-1.675-2.1zM8.247 22.177l.437 1.525-3.249 4.16-.422-3.557zM5.069 6.292v4.804h-1.82c-.141 0-.278-.018-.41-.052a1.723 1.723 0 01-1.281-1.678V6.292h3.51zM10.113 35l5.45-9.816h3.18L11.685 35z" />
        </g>
        <path
          d="M5.069 8.94v2.156h-1.82c-.141 0-.278-.018-.41-.052V9.948a.998.998 0 01.987-1.009h1.243z"
          fill="#5EBC77"
        />
        <path fill="#FFA700" d="M1.422 5.471l-.6-4.433L2.417 2.31 3.59 0l.758 2.17 1.456-1.132-.507 4.433" />
        <path
          d="M14.342 14.381c-2.025 0-3.667 1.68-3.667 3.75v1.821c0 2.071 1.642 3.75 3.667 3.75 2.026 0 3.668-1.679 3.668-3.75v-1.821c0-2.07-1.642-3.75-3.668-3.75z"
          fill="#5EBC77"
        />
        <g fill="#368A50">
          <path d="M11.932 19.952v-1.821c0-1.852 1.313-3.389 3.04-3.694a3.605 3.605 0 00-.63-.056c-2.025 0-3.667 1.679-3.667 3.75v1.821c0 2.071 1.642 3.75 3.667 3.75.215 0 .425-.02.63-.056-1.727-.305-3.04-1.842-3.04-3.694z" />
          <path d="M18.01 18.131v1.821l-.02.002a3.77 3.77 0 00-1.054-2.26 3.617 3.617 0 00-2.594-1.098c-1.96 0-3.562 1.573-3.662 3.552a3.684 3.684 0 01-.005-.196v-1.82c0-1.036.411-1.974 1.074-2.653a3.617 3.617 0 012.593-1.098c2.026 0 3.668 1.679 3.668 3.75z" />
        </g>
        <path fill="#47A562" d="M.065 5.435h6.497v1.714H.065z" />
        <g fill="#368A50">
          <path d="M.065 5.435h2.783V7.15H.065zM16.455 18.998l-1.993-.478h-.005a.504.504 0 00-.043-.008l-.014-.003a.516.516 0 00-.115 0c-.005 0-.01.002-.014.003a.532.532 0 00-.043.007l-.005.001-1.994.478a.536.536 0 00-.39.643.524.524 0 00.63.4l1.35-.323v.801a.53.53 0 00.524.536.53.53 0 00.523-.536v-.801l1.35.323a.527.527 0 00.63-.4.537.537 0 00-.39-.643zM20.889 31.026l-2.892 2.708 3.379-1.762M11.932 25.184l-2.039 2.978.782-3.551" />
        </g>
      </g>
    </svg>
  );
}
